/**
 * Alarm history for one patient.
 *
 * The RECORD is the server's (live_alerts, written by lib/liveAlarmEngine.js
 * for every streaming device whether or not anyone is watching):
 *   GET /api/live/alerts/:deviceId?from&to
 *
 * This module also keeps a small in-memory log of what THIS browser session
 * saw. It is only a fallback — shown, clearly labelled, when the server history
 * cannot be reached (backend not updated yet, offline) and in demo mode. It is
 * not a record: it starts when the page opens and dies with the tab.
 */
import axios from 'axios';
import { liveApi } from '../../config/config';

const UNITS = { hr: 'bpm', spo2: '%', resp: 'rpm', temp: '°C', ecgBatt: '%', spo2Batt: '%', mobileBatt: '%' };
const MAX_LOCAL = 300;
const local = new Map();   // deviceId -> [episode], newest first

/** Called by the card whenever its set of active alarms changes. */
export function recordLocal(deviceId, alerts, vitals) {
  let list = local.get(deviceId);
  if (!list) { list = []; local.set(deviceId, list); }
  const now = Date.now();
  const active = {};
  (alerts || []).forEach(a => { active[a.key] = a; });
  list.forEach(ep => { if (ep.cleared == null && !active[ep.code]) ep.cleared = now; });
  Object.keys(active).forEach(key => {
    if (list.some(ep => ep.cleared == null && ep.code === key)) return;
    const a = active[key];
    const v = a.param && vitals ? vitals[a.param] : null;
    list.unshift({
      id: 'local-' + now + '-' + key, code: key, label: a.label, priority: a.priority || 'medium',
      severity: a.severity, onset: now, cleared: null,
      param: a.param || null, unit: a.param ? (UNITS[a.param] || '') : '',
      value: v != null && !isNaN(v) ? Math.round(v * 10) / 10 : null, worst: null, limit: null, op: null,
    });
  });
  if (list.length > MAX_LOCAL) list.length = MAX_LOCAL;
}

export function getLocalHistory(deviceId, rangeMs) {
  const from = Date.now() - rangeMs;
  return (local.get(deviceId) || [])
    .filter(ep => ep.cleared == null || ep.cleared >= from)
    .map(ep => Object.assign({}, ep, {
      ongoing: ep.cleared == null,
      durationSec: Math.max(0, Math.round(((ep.cleared == null ? Date.now() : ep.cleared) - ep.onset) / 1000)),
    }));
}

/** Server history. Rejects if the server cannot be reached / has no such route. */
export function fetchHistory(deviceId, rangeMs) {
  const to = Date.now();
  return axios.get(`${liveApi}api/live/alerts/${encodeURIComponent(deviceId)}`, { params: { from: to - rangeMs, to: to } })
    .then(res => ({
      alerts: (res.data && Array.isArray(res.data.alerts)) ? res.data.alerts : [],
      truncated: !!(res.data && res.data.truncated),
    }));
}

export function fmtDuration(sec) {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m ${String(sec % 60).padStart(2, '0')}s`;
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
}

/** "165 bpm · peak 171 · limit above 150" */
export function describeValue(ep) {
  const parts = [];
  if (ep.value != null) parts.push(`${ep.value} ${ep.unit || ''}`.trim());
  if (ep.worst != null && ep.worst !== ep.value) parts.push(`${ep.op === 'lt' ? 'lowest' : 'peak'} ${ep.worst}`);
  if (ep.limit != null) parts.push(`limit ${ep.op === 'lt' ? 'below' : 'above'} ${ep.limit}`);
  if (ep.event && !parts.length) parts.push('rhythm event');
  if (ep.replayed) parts.push('received late (phone was offline)');
  if (ep.closedBy) parts.push(ep.closedBy);
  return parts.join(' · ');
}
